import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, Observable, of, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { MainService } from "../../../app.service";
import { ExtractionRun, ExtractionRunFilters, ExtractionRunListResult } from "../interfaces/extraction-run";
import { ExtractAcceptedResponse, ExtractRequest } from "../interfaces/extract-request";
import { Issue, IssueFilters } from "../interfaces/issue";
import { IssueListResult } from "../interfaces/sonar-response";
import { ConsolidatedRow, ConsolidatedSummary } from "../interfaces/consolidated-report";
import { Project, ProjectFilters, ProjectListResult, UpdateProjectInput } from "../interfaces/project";
import { ConsolidatedListResult, DailyReportListResult, DailyReportRow, ReportFilters, RuleReportRow } from "../interfaces/reports";import {
  AddReleaseRulesRequest,
  CreateReleaseInput,
  Release,
  ReleaseFilters,
  ReleaseListResult,
  ReleaseRule,
  Rule,
  RuleFilters,
  RuleListResult,
  UpdateReleaseInput,
} from '../interfaces/release';
@Injectable({
  providedIn: "root",
})
export class SonarExtractorService {
  private readonly BASE_URL = `${environment.SONAR_EXTRACTOR_API}api/v1`;

  /** Emitido quando uma extração é concluída (sucesso ou falha) */
  private readonly _extractionCompleted$ = new Subject<void>();
  readonly extractionCompleted$ = this._extractionCompleted$.asObservable();

  /** Dispara o evento de extração concluída */
  notifyExtractionCompleted(): void {
    this._extractionCompleted$.next();
  }

  constructor(
    private httpClient: HttpClient,
    private mainService: MainService
  ) {}

  /** GETs — sem Content-Type para não disparar CORS preflight */
  private get httpGetOptions(): { headers: HttpHeaders } {
    const token = this.mainService.userToken.token || '';
    return {
      headers: new HttpHeaders({
        'Authorization': `Bearer ${token}`
      })
    };
  }

  /** POST/PUT — com Content-Type pois há body JSON */
  private get httpWriteOptions(): { headers: HttpHeaders } {
    const token = this.mainService.userToken.token || '';
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      })
    };
  }

  // --- Extração ---

  /**
   * GET /api/v1/extract/runs
   * Lista execuções de extração, da mais recente para a mais antiga.
   * Filtros opcionais: startDate/endDate atuam sobre started_at; status filtra por RUNNING|SUCCESS|FAILED.
   */
  getExtractionRuns(filters?: ExtractionRunFilters): Observable<ExtractionRunListResult> {
    return this.httpClient.get<ExtractionRunListResult>(
      `${this.BASE_URL}/extract/runs`,
      { ...this.httpGetOptions, params: this.buildExtractionRunParams(filters) }
    );
  }

  /**
   * GET /api/v1/extract/runs/export
   * Faz download das execuções de extração filtradas em formato CSV.
   */
  exportExtractionRunsCsv(filters?: ExtractionRunFilters): Observable<Blob> {
    return this.httpClient.get(`${this.BASE_URL}/extract/runs/export`, {
      ...this.httpGetOptions,
      params:       this.buildExtractionRunParams(filters),
      responseType: 'blob',
    });
  }

  /**
   * GET /api/v1/extract/{id}
   * Retorna o ExtractionRun completo para polling após o POST /extract.
   */
  getExtractionRunById(id: number): Observable<ExtractionRun> {
    return this.httpClient.get<ExtractionRun>(
      `${this.BASE_URL}/extract/${id}`,
      this.httpGetOptions
    );
  }

  /**
   * POST /api/v1/extract
   * Aciona uma extração assíncrona. Retorna HTTP 202 com o run_id.
   */
  postExtract(request: ExtractRequest): Observable<ExtractAcceptedResponse> {
    return this.httpClient.post<ExtractAcceptedResponse>(
      `${this.BASE_URL}/extract`,
      request,
      this.httpWriteOptions
    );
  }

  // --- Issues ---

  /**
   * GET /api/v1/issues
   * Lista issues com paginação e filtros opcionais.
   */
  getIssues(filters?: IssueFilters): Observable<IssueListResult<Issue>> {
    return this.httpClient.get<IssueListResult<Issue>>(
      `${this.BASE_URL}/issues`,
      { ...this.httpGetOptions, params: this.buildIssueParams(filters) }
    );
  }

  /**
   * GET /api/v1/issues/export
   * Faz download das issues filtradas em formato CSV.
   */
  exportIssuesCsv(filters?: IssueFilters): Observable<Blob> {
    return this.httpClient.get(`${this.BASE_URL}/issues/export`, {
      ...this.httpGetOptions,
      params:       this.buildIssueParams(filters),
      responseType: 'blob',
    });
  }

  // --- Projetos ---

  /**
   * GET /api/v1/projects
   * Lista projetos cadastrados, com filtro opcional por sonar_key ou nome.
   */
  getProjects(filters?: ProjectFilters): Observable<Array<Project>> {
    const params: Record<string, string | number> = {};

    if (filters?.projectKey) params["projectKey"] = filters.projectKey;
    if (filters?.name)       params["name"]       = filters.name;
    if (filters?.page)       params["page"]       = filters.page;
    if (filters?.limit)      params["limit"]      = filters.limit;

    return this.httpClient.get<Array<Project> | ProjectListResult>(
      `${this.BASE_URL}/projects`,
      { ...this.httpGetOptions, params }
    ).pipe(
      map(res => {
        if (Array.isArray(res)) return res as Project[];
        // Backend retorna wrapper paginado { projects: [...], total, ... }
        const wrapped = res as ProjectListResult;
        return Array.isArray(wrapped?.projects) ? wrapped.projects : [];
      })
    );
  }

  /**
   * Busca todos os projetos em uma única chamada (limit 500 é suficiente para
   * o volume atual de ~267 projetos).
   * Suporta resposta paginada (ProjectListResult) e array plano (Project[]).
   */
  getAllProjects(): Observable<Project[]> {
    return this.httpClient.get<Project[] | ProjectListResult>(
      `${this.BASE_URL}/projects`,
      { ...this.httpGetOptions, params: { page: 1, limit: 500 } }
    ).pipe(
      map(res => {
        if (Array.isArray(res)) return res as Project[];
        const wrapped = res as ProjectListResult;
        return Array.isArray(wrapped?.projects) ? wrapped.projects : [];
      })
    );
  }

  /**
   * PATCH /api/v1/projects/{sonarKey}
   * Atualiza parcialmente os metadados de negócio de um projeto (segment, area, squad, module, tribe, po).
   */
  updateProject(sonarKey: string, input: UpdateProjectInput): Observable<Project> {
    return this.httpClient.patch<Project>(
      `${this.BASE_URL}/projects/${encodeURIComponent(sonarKey)}`,
      input,
      this.httpWriteOptions
    );
  }

  // --- Relatórios ---

  /**
   * GET /api/v1/reports/summary
   * Retorna totais agregados em SQL — sem paginação.
   * Usado exclusivamente pelos KPIs do dashboard.
   */
  getConsolidatedSummary(filters?: ReportFilters): Observable<ConsolidatedSummary> {
    const params = this.buildReportParams(filters);
    return this.httpClient.get<ConsolidatedSummary>(
      `${this.BASE_URL}/reports/summary`,
      { ...this.httpGetOptions, params }
    );
  }

  /**
   * GET /api/v1/reports/consolidated
   * Relatório consolidado por projeto e regra (estado atual da última extração).
   * startDate/endDate são opcionais — o backend usa a data da última extração se omitidos.
   */
  getConsolidatedReport(filters?: ReportFilters): Observable<Array<ConsolidatedRow>> {
    const params = this.buildReportParams(filters);

    return this.httpClient.get<Array<ConsolidatedRow> | ConsolidatedListResult>(
      `${this.BASE_URL}/reports/consolidated`,
      { ...this.httpGetOptions, params }
    ).pipe(
      map(data => {
        if (Array.isArray(data)) return data as ConsolidatedRow[];
        // Fallback: backend retornou wrapper paginado { rows: [...] }
        const wrapped = data as ConsolidatedListResult;
        return Array.isArray(wrapped?.rows) ? wrapped.rows : [];
      })
    );
  }

  /**
   * GET /api/v1/reports/by-rule
   * Relatório de issues abertas agrupadas por regra e projeto no período informado.
   * startDate e endDate são obrigatórios.
   */
  getReportByRule(filters: Required<Pick<ReportFilters, "startDate" | "endDate">> & ReportFilters): Observable<Array<RuleReportRow>> {
    const params = this.buildReportParams(filters);

    return this.httpClient.get<Array<RuleReportRow> | { rows?: RuleReportRow[] }>(
      `${this.BASE_URL}/reports/by-rule`,
      { ...this.httpGetOptions, params }
    ).pipe(
      map(data => {
        if (Array.isArray(data)) return data as RuleReportRow[];
        // Fallback: backend retornou wrapper paginado { rows: [...] }
        return Array.isArray(data?.rows) ? data.rows : [];
      })
    );
  }

  /**
   * GET /api/v1/reports/daily
   * Contagem de issues abertas por dia de extração no período informado.
   * startDate e endDate são obrigatórios.
   */
  getReportDaily(filters: Required<Pick<ReportFilters, "startDate" | "endDate">> & ReportFilters): Observable<DailyReportListResult> {
    const params = this.buildReportParams(filters);

    return this.httpClient.get<DailyReportListResult>(
      `${this.BASE_URL}/reports/daily`,
      { ...this.httpGetOptions, params }
    );
  }

  /**
   * GET /api/v1/reports/consolidated/export
   * Faz download do relatório consolidado em formato CSV.
   */
  exportConsolidatedCsv(filters?: ReportFilters): Observable<Blob> {
    const params = this.buildReportParams(filters);

    return this.httpClient.get(`${this.BASE_URL}/reports/consolidated/export`, {
      ...this.httpGetOptions,
      params,
      responseType: "blob",
    });
  }

  /**
   * GET /api/v1/reports/by-rule/export
   * Faz download do relatório por regra em formato CSV.
   */
  exportReportByRuleCsv(filters?: ReportFilters): Observable<Blob> {
    const params = this.buildReportParams(filters);

    return this.httpClient.get(`${this.BASE_URL}/reports/by-rule/export`, {
      ...this.httpGetOptions,
      params,
      responseType: "blob",
    });
  }

  // --- Releases ---

  /**
   * GET /api/v1/releases
   * Lista todas as releases com paginação.
   */
  getReleases(filters?: ReleaseFilters): Observable<ReleaseListResult> {
    const params: Record<string, string | number> = {};
    if (filters?.page)  params['page']  = filters.page;
    if (filters?.limit) params['limit'] = filters.limit;
    return this.httpClient.get<ReleaseListResult>(
      `${this.BASE_URL}/releases`,
      { ...this.httpGetOptions, params }
    );
  }

  /**
   * GET /api/v1/releases/{id}
   * Retorna a release pelo ID incluindo regras associadas.
   */
  getReleaseById(id: number): Observable<Release> {
    return this.httpClient.get<Release>(
      `${this.BASE_URL}/releases/${id}`,
      this.httpGetOptions
    );
  }

  /**
   * POST /api/v1/releases
   * Cria uma nova release.
   */
  createRelease(input: CreateReleaseInput): Observable<Release> {
    return this.httpClient.post<Release>(
      `${this.BASE_URL}/releases`,
      input,
      this.httpWriteOptions
    );
  }

  /**
   * PUT /api/v1/releases/{id}
   * Atualiza os campos de uma release.
   */
  updateRelease(id: number, input: UpdateReleaseInput): Observable<Release> {
    return this.httpClient.put<Release>(
      `${this.BASE_URL}/releases/${id}`,
      input,
      this.httpWriteOptions
    );
  }

  /**
   * DELETE /api/v1/releases/{id}
   * Remove uma release e desvincula todas as suas regras (CASCADE).
   */
  deleteRelease(id: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.BASE_URL}/releases/${id}`,
      this.httpWriteOptions
    );
  }

  /**
   * GET /api/v1/releases/{id}/rules
   * Retorna as regras associadas à release com seu goal.
   */
  getReleaseRules(id: number): Observable<ReleaseRule[]> {
    return this.httpClient.get<ReleaseRule[]>(
      `${this.BASE_URL}/releases/${id}/rules`,
      this.httpGetOptions
    );
  }

  /**
   * POST /api/v1/releases/{id}/rules
   * Vincula/atualiza regras à release.
   */
  addReleaseRules(id: number, rules: AddReleaseRulesRequest[]): Observable<void> {
    return this.httpClient.post<void>(
      `${this.BASE_URL}/releases/${id}/rules`,
      rules,
      this.httpWriteOptions
    );
  }

  /**
   * PUT /api/v1/releases/{id}/rules/{ruleID}
   * Atualiza o goal de um vínculo existente.
   */
  updateReleaseRuleGoal(id: number, ruleId: number, rulekey: string, goal: string): Observable<ReleaseRule> {
    return this.httpClient.put<ReleaseRule>(
      `${this.BASE_URL}/releases/${id}/rules/${ruleId}`,
      { rulekey, goal },
      this.httpWriteOptions
    );
  }

  /**
   * DELETE /api/v1/releases/{id}/rules/{ruleID}
   * Desvincula uma regra da release.
   */
  deleteReleaseRule(id: number, ruleId: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.BASE_URL}/releases/${id}/rules/${ruleId}`,
      this.httpWriteOptions
    );
  }

  // --- Catálogo de Regras ---

  /**
   * GET /api/v1/rules
   * Lista regras do catálogo com filtros e paginação.
   */
  getRules(filters?: RuleFilters): Observable<RuleListResult> {
    const params: Record<string, string | number> = {};
    if (filters?.lang)     params['lang']     = filters.lang;
    if (filters?.severity) params['severity'] = filters.severity;
    if (filters?.type)     params['type']     = filters.type;
    if (filters?.status)   params['status']   = filters.status;
    if (filters?.search)   params['search']   = filters.search;
    if (filters?.page)     params['page']     = filters.page;
    if (filters?.limit)    params['limit']    = filters.limit;
    return this.httpClient.get<RuleListResult>(
      `${this.BASE_URL}/rules`,
      { ...this.httpGetOptions, params }
    );
  }

  // --- Helpers privados ---

  /** Constrói os parâmetros de query para os endpoints de execuções de extração */
  private buildExtractionRunParams(filters?: ExtractionRunFilters): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    if (filters?.startDate) params["startDate"] = filters.startDate;
    if (filters?.endDate)   params["endDate"]   = filters.endDate;
    if (filters?.status)    params["status"]    = filters.status;
    if (filters?.page)      params["page"]      = filters.page;
    if (filters?.limit)     params["limit"]     = filters.limit;
    return params;
  }

  /** Constrói os parâmetros de query para os endpoints de issues */
  private buildIssueParams(filters?: IssueFilters): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    if (filters?.projectKey) params['projectKey'] = filters.projectKey;
    if (filters?.ruleKey)    params['ruleKey']    = filters.ruleKey;
    if (filters?.po)         params['po']         = filters.po;
    if (filters?.goal)       params['goal']       = filters.goal;
    if (filters?.squad)      params['squad']      = filters.squad;
    if (filters?.module)     params['module']     = filters.module;
    if (filters?.tribe)      params['tribe']      = filters.tribe;
    if (filters?.releaseId)  params['releaseId']  = filters.releaseId;
    if (filters?.page)       params['page']       = filters.page;
    if (filters?.limit)      params['limit']      = filters.limit;
    return params;
  }

  private buildReportParams(filters?: ReportFilters): Record<string, string | number> {
    const params: Record<string, string | number> = {};

    if (filters?.startDate)  params["startDate"]  = filters.startDate;
    if (filters?.endDate)    params["endDate"]    = filters.endDate;
    if (filters?.severity)   params["severity"]   = filters.severity;
    if (filters?.projectKey) params["projectKey"] = filters.projectKey;
    if (filters?.ruleKey)    params["ruleKey"]    = filters.ruleKey;
    if (filters?.page)       params["page"]       = filters.page;
    if (filters?.limit)      params["limit"]      = filters.limit;

    return params;
  }
}
